import { useNavigate } from "react-router-dom";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import ScrollProgress from "@/components/site/ScrollProgress";
import { motion } from "framer-motion";
import { SectionEyebrow, SectionTitle, FadeUp } from "@/components/site/_shared";
import useDocumentMeta from "@/hooks/useDocumentMeta";

const FORM_URL = process.env.REACT_APP_JOTFORM_INTAKE_URL;
const PREVIEW_IMG = process.env.REACT_APP_CONTACT_PREVIEW_IMG;

const ROUTES = [
  {
    eyebrow: "AI Health Concierge",
    title: "Talk to the Integrated Wellness Agent",
    body: "An always-on guide that listens, asks the right questions, and maps a personalized path.",
    href: "/agents/wellness",
    cta: "Start the conversation",
    internal: true,
  },
  {
    eyebrow: "Integrated Optimization Agent",
    title: "Ask Rhea",
    body: "Discuss your goals, symptoms, and optimization priorities — Rhea guides you to the right next step.",
    href: "/agents/rhea",
    cta: "Launch Rhea",
    internal: true,
  },
];

export default function Contact() {
  const navigate = useNavigate();

  useDocumentMeta({
    title: "Contact — Integrated Wellness",
    description:
      "Start your free health assessment, talk to our AI health concierge, or reach the Integrated Wellness team. Organizations: ask about clinical-intelligence infrastructure.",
    image: PREVIEW_IMG,
  });

  return (
    <div data-testid="contact-page" className="min-h-screen bg-background">
      <ScrollProgress />
      <Header />
      <main className="pt-32 md:pt-36 pb-20 md:pb-28">
        <section className="max-w-6xl mx-auto px-4 md:px-8">
          <FadeUp className="text-center max-w-3xl mx-auto">
            <SectionEyebrow>Get in Touch</SectionEyebrow>
            <h1 className="font-display font-semibold tracking-[-0.025em] text-balance leading-[1.04] text-[clamp(2.25rem,5.5vw,4rem)] mt-5">
              Let&rsquo;s build <em className="not-italic text-primary">your plan</em>.
            </h1>
            <p className="mt-7 text-lg md:text-xl text-foreground/80 leading-relaxed">
              Start the free health assessment — a guided form that captures your history,
              goals, and context in minutes. Or talk to one of our AI agents below.
              Practices, gyms, employers and wellness partners: reach out about
              clinical-intelligence infrastructure for your organization.
            </p>
          </FadeUp>

          {/* Primary intake — mirrors ClosingCTA */}
          <FadeUp className="mt-12">
            <motion.a
              href={FORM_URL}
              data-testid="contact-form-preview"
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="group block max-w-3xl mx-auto rounded-3xl overflow-hidden border-2 border-primary/40
                         bg-card shadow-2xl hover:border-primary hover:shadow-[0_28px_70px_-18px_hsl(218_70%_28%/0.45)]
                         hover:-translate-y-1 transition-all"
            >
              <div className="grid grid-cols-1 sm:grid-cols-12 items-stretch">
                <div className="sm:col-span-5 relative aspect-[4/3] sm:aspect-auto overflow-hidden bg-foreground">
                  <img
                    src={PREVIEW_IMG}
                    alt="Medical Wellness Intake Form preview — begin your Superhuman Journey with Integrated Clinical Intelligence"
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    loading="lazy"
                    style={{ filter: "brightness(1.05) contrast(1.05) saturate(1.1)" }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-tr from-foreground/40 via-transparent to-transparent" />
                </div>
                <div className="sm:col-span-7 p-7 md:p-9 flex flex-col justify-center">
                  <div className="font-mono text-[0.65rem] uppercase tracking-[0.22em] text-primary mb-3">
                    Free Health Assessment
                  </div>
                  <h2 className="font-display text-2xl md:text-3xl font-semibold leading-tight m-0">
                    Start My Health Assessment
                  </h2>
                  <p className="mt-3 text-base text-foreground/75 leading-relaxed">
                    A guided intake — your history, goals, and context — captured in minutes.
                  </p>
                  <span
                    className="mt-6 iw-btn-red inline-flex items-center gap-2 px-7 py-3.5 text-[0.7rem] bg-accent
                               text-primary-foreground text-sm font-semibold w-fit
                               group-hover:gap-3 transition-all"
                  >
                    Start My Health Assessment
                    <span aria-hidden className="text-base">→</span>
                  </span>
                </div>
              </div>
            </motion.a>
          </FadeUp>

          {/* Other routes */}
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-5 max-w-4xl mx-auto">
            {ROUTES.map((r, i) => (
              <FadeUp key={r.href} delay={i * 0.08}>
                <button
                  type="button"
                  data-testid={`contact-route-${r.href.replace(/[^a-z0-9]+/gi, "-")}`}
                  onClick={() => navigate(r.href)}
                  className="group h-full w-full text-left p-7 bg-card border-2 border-border rounded-2xl
                             hover:border-primary/55 hover:-translate-y-0.5 hover:shadow-lg transition-all"
                >
                  <div className="font-mono text-[0.6rem] uppercase tracking-[0.22em] text-primary/80 mb-3">
                    {r.eyebrow}
                  </div>
                  <h3 className="font-display text-xl md:text-2xl font-semibold leading-tight">
                    {r.title}
                  </h3>
                  <p className="mt-3 text-base text-foreground/75 leading-relaxed">
                    {r.body}
                  </p>
                  <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-primary group-hover:gap-3 transition-all">
                    {r.cta} <span aria-hidden>→</span>
                  </span>
                </button>
              </FadeUp>
            ))}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
